import * as React from 'react'
import { OrgPersonalSkillsTable } from './table'
import { TableProvider } from './table/context/table'
import { deserialize } from '../../api/utils/deserialize'
import { OrgPersonalSkillsTableRow, UserWithProficiency } from './table/data'
import { observer } from 'mobx-react-lite'
import { useApiClient } from '../../utils/use-api-client'
import { errorToast } from '../../utils/error-toast'

export const OrgPersonalSkillsPage = observer(() => {
  const [allLoading, setAllLoading] = React.useState(true)
  const [data, setData] = React.useState<OrgPersonalSkillsTableRow[]>([])
  const client = useApiClient()

  const fetchAllProfileSkills = React.useCallback(async () => {
    try {
      const json = await client.get('/api/v1/profile_skill_insights')
      const deserialized = deserialize(json)
      if (!deserialized?.data) {
        setData([])
        return
      }
      const insights = deserialized.data.profileSkillInsights as
        | Record<string, OrgPersonalSkillsTableRow>
        | undefined
      setData(insights ? Object.values(insights).map(normalizeRow) : [])
    } catch {
      errorToast()
      setData([])
    } finally {
      setAllLoading(false)
    }
  }, [client])

  React.useEffect(() => {
    fetchAllProfileSkills()
  }, [fetchAllProfileSkills])

  return (
    <TableProvider loading={allLoading} data={data}>
      <OrgPersonalSkillsTable />
    </TableProvider>
  )
})

type CamelizedUserWithProficiency = {
  userId: number
  proficiencyLevel: number | null
  source: 'implied' | 'assigned' | 'self_declared'
}

function normalizeRow(
  row: OrgPersonalSkillsTableRow & Record<string, unknown>
): OrgPersonalSkillsTableRow {
  return {
    ...row,
    first3UserIds: row.first3UserIds
      ? row.first3UserIds.map((id) => id.toString())
      : [],
    usersWithProficiency: row.usersWithProficiency
      ? (
          row.usersWithProficiency as unknown as CamelizedUserWithProficiency[]
        ).map(
          (u): UserWithProficiency => ({
            userId: u.userId?.toString(),
            proficiencyLevel: u.proficiencyLevel,
            source: u.source,
          })
        )
      : [],
  }
}
